'use client';

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Tooltip } from 'src/components';
import { useSelector } from 'react-redux';
import {
  selectDiscoveryMapHeatMap,
  selectDiscoveryMapZoom,
  selectDiscoveryMapSelectedHeatmapMetric,
  selectDiscoveryMapCurrentTerritory,
  selectDiscoveryMapMapBounds,
  selectAdvancedSearchRadius,
} from 'src/redux/slices/discoveryMapSlice';
import { useIsAdvancedSearchActive } from 'src/hooks/discovery';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
import { useGetAdvancedSearchHeatmapQuery } from 'src/api/redux/advancedSearches';
import type { AdvancedSearchHeatmapPoint } from 'src/api/redux/advancedSearches';
import MedMapDeckOverlay from 'src/components/Maps/MedMapDeckOverlay';
import { createHeatmapLayer } from 'src/components/Maps/heatmapLayer';
import { useGetClusterObjects } from './hooks';
import { MapLoadingIndicator } from '../MapLoadingIndicator';
import buildAdvancedSearchMapParams from '../../utils/buildAdvancedSearchMapParams';
import { useDebounce } from 'use-debounce';

const MedMapContent = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const heatMap = useSelector(selectDiscoveryMapHeatMap);
  const zoom = useSelector(selectDiscoveryMapZoom);
  const selectedHeatmapMetric = useSelector(
    selectDiscoveryMapSelectedHeatmapMetric
  );
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const mapBounds = useSelector(selectDiscoveryMapMapBounds);
  const advancedSearchRadius = useSelector(selectAdvancedSearchRadius);

  const isAdvancedSearch = useIsAdvancedSearchActive();
  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);

  const [tooltipInfo, setTooltipInfo] = useState({ x: 0, y: 0, name: '' });
  const tooltipInfoRef = useRef(tooltipInfo);
  tooltipInfoRef.current = tooltipInfo;

  // Clear tooltip when mouse leaves the map container
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!tooltipInfoRef.current.name) return;

      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const isInsideContainer =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      if (!isInsideContainer) {
        setTooltipInfo({ x: 0, y: 0, name: '' });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // --- Discovery heatmap data (existing flow) ---
  const activeMarker = null;
  const { clusterObjects } = useGetClusterObjects(activeMarker);

  // --- Advanced search heatmap data ---
  const advSearchHeatmapParams = useMemo(() => {
    if (!isAdvancedSearch || !heatMap) return null;
    return buildAdvancedSearchMapParams({
      advancedSearchId: appliedSavedVolume.id,
      zoom,
      currentTerritoryId: currentTerritory?.id,
      mapBounds,
      overrides: {
        heatmap: true,
        ...(selectedHeatmapMetric?.code
          ? { metric: selectedHeatmapMetric.code }
          : {}),
      },
      advancedSearchRadius,
    });
  }, [
    isAdvancedSearch,
    heatMap,
    appliedSavedVolume,
    zoom,
    selectedHeatmapMetric?.code,
    currentTerritory?.id,
    mapBounds,
    advancedSearchRadius,
  ]);

  const { data: advSearchHeatmapData, isFetching: isAdvSearchHeatmapFetching } =
    useGetAdvancedSearchHeatmapQuery(advSearchHeatmapParams, {
      skip: !isAdvancedSearch || !heatMap || !advSearchHeatmapParams,
    });

  // Transform advanced search heatmap results into GeoJSON-like objects
  const advSearchHeatmapObjects = useMemo(() => {
    if (!advSearchHeatmapData?.results) return undefined;
    return advSearchHeatmapData.results.map(
      (point: AdvancedSearchHeatmapPoint) => ({
        geometry: {
          type: 'Point',
          coordinates: [point.lng, point.lat],
        },
        properties: {
          volume: point.volume ?? 0,
        },
      })
    );
  }, [advSearchHeatmapData?.results]);

  // Choose data source based on mode
  const heatmapData = isAdvancedSearch
    ? advSearchHeatmapObjects
    : clusterObjects;
  const [debouncedZoom] = useDebounce(zoom, 700);

  // Memoize all layers to avoid unnecessary recalculations
  const allLayers = useMemo(() => {
    const layersToRender = [];

    // Add heatmap layer if enabled
    if (heatMap && heatmapData && heatmapData.length > 0) {
      const heatmapLayer = createHeatmapLayer({
        id: `heatmap-layer-${selectedHeatmapMetric?.code ?? 'default'}`,
        data: heatmapData,
        zoom: debouncedZoom,
        getWeight: (d: any) => {
          if (isAdvancedSearch) {
            const volume = d.properties?.volume;
            return Number.isFinite(Number(volume)) ? Number(volume) : 0;
          }
          const claims = d.properties?.claims;
          return Number.isFinite(Number(claims)) ? Number(claims) : 0;
        },
      });

      layersToRender.push(heatmapLayer);
    }

    return layersToRender;
  }, [
    heatMap,
    isAdvancedSearch,
    heatmapData,
    debouncedZoom,
    selectedHeatmapMetric?.code,
  ]);

  const isHeatmapLoading =
    isAdvancedSearch && heatMap && isAdvSearchHeatmapFetching;

  return (
    <div className="absolute inset-0 pointer-events-none" ref={containerRef}>
      {/* Loading indicator for advanced search heatmap */}
      {isHeatmapLoading && (
        <div className="pointer-events-auto">
          <MapLoadingIndicator message="Loading heatmap..." />
        </div>
      )}

      {/* Unified layer rendering with a single MedMapDeckOverlay */}
      {allLayers.length > 0 && <MedMapDeckOverlay layers={allLayers} />}

      {/* PulseUI Tooltip */}
      <Tooltip
        content={tooltipInfo.name}
        position="top"
        isVisible={
          tooltipInfo.x > 0 && tooltipInfo.y > 0 && Boolean(tooltipInfo.name)
        }
        x={tooltipInfo.x}
        y={tooltipInfo.y - 35}
        slotProps={{
          containerClassName: 'bg-neutral-600 text-white font-semibold text-sm',
          containerStyle: {
            position: 'fixed',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          },
        }}
      >
        <span />
      </Tooltip>
    </div>
  );
};

export default MedMapContent;
